import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useAdminDashboard = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [attendeeData, setAttendeeData] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [administrators, setAdministrators] = useState([]);
  const [mondaySyncStatus, setMondaySyncStatus] = useState(null);
  const [changeAlerts, setChangeAlerts] = useState([]);
  const [alertRecipients, setAlertRecipients] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [crewVisibility, setCrewVisibility] = useState('Forum');
  const [scrapedEvents, setScrapedEvents] = useState([]);
  const [attendanceRequests, setAttendanceRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sortField, setSortField] = useState(null);  // null = smart sorting
  const [sortDir, setSortDir] = useState('asc');
  const [expandedRows, setExpandedRows] = useState({});
  const tableRef = React.useRef(null);
  const [attendeeSearch, setAttendeeSearch] = useState("");
  const [attendeeSortField, setAttendeeSortField] = useState("event_date");
  const [attendeeSortDir, setAttendeeSortDir] = useState("asc");
  const [uploading, setUploading] = useState(false);
  const [syncingMonday, setSyncingMonday] = useState(false);
  const fileInputRef = useRef(null);
  const [editingEventId, setEditingEventId] = useState(null);
  const [editEventData, setEditEventData] = useState({});
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawAttendanceId, setWithdrawAttendanceId] = useState(null);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [withdrawingAttendance, setWithdrawingAttendance] = useState(null);
  const [currentAdminEmail, setCurrentAdminEmail] = useState("");
  // Revoke approval dialog state
  const [revokeApprovalDialogOpen, setRevokeApprovalDialogOpen] = useState(false);
  const [revokeParticipantId, setRevokeParticipantId] = useState(null);
  const [revokeParticipantName, setRevokeParticipantName] = useState("");
  const [revokeReason, setRevokeReason] = useState("");
  const [archivedApplicants, setArchivedApplicants] = useState([]);
  const [archiveStats, setArchiveStats] = useState({ active: 0, archived: 0, new: 0, welcomed: 0 });
  const [emailForm, setEmailForm] = useState({
    recipient_type: 'confirmed_attendees',
    subject: '',
    body: '',
    additional_recipients: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [newEvent, setNewEvent] = useState({
    name: "",
    date: "",
    call_time: "",
    description: ""
  });
  const handleEditEvent = (event) => {
    setEditingEventId(event.id);
    setEditEventData({
      call_time: event.call_time || '',
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      location: event.location || '',
      notes: event.notes || event.description || '',
      website_url: event.website_url || ''
    });
  };

  const handleSaveEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/events/${eventId}`, editEventData, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Event updated! Changes will reflect on all screens.");
      setEditingEventId(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update event");
    }
  };

  const handleSyncMonday = async () => {
    setSyncingMonday(true);
    try {
      const token = localStorage.getItem('adminToken');
      const { data } = await axios.post(`${API}/admin/sync-monday-events`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message || "Monday.com sync failed");
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync from Monday.com");
    } finally {
      setSyncingMonday(false);
    }
  };


  const handleCancelEdit = () => {
    setEditingEventId(null);
    setEditEventData({});
  };

  const handleCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/upload-csv`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });
      
      const { imported, skipped, errors } = response.data;
      
      if (imported > 0) {
        toast.success(`Successfully imported ${imported} events`);
      }
      if (skipped > 0) {
        toast.info(`Skipped ${skipped} duplicate/invalid entries`);
      }
      if (errors && errors.length > 0) {
        console.warn("CSV Import errors:", errors);
      }
      
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload CSV");
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    // Check if admin is authenticated
    const token = localStorage.getItem('adminToken');
    if (!token) {
      toast.error("Please login first");
      navigate('/admin/login');
      return;
    }

    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      };

      // Get current admin's email from token payload or session
      const adminEmail = localStorage.getItem('adminEmail');
      if (adminEmail) {
        setCurrentAdminEmail(adminEmail.toLowerCase());
      }

      const [eventsRes, attendeesRes, participantsRes, scrapedRes, requestsRes, adminsRes, syncRes, alertsRes, alertSettingsRes, templatesRes, applicantsRes, crewVisRes, archivedRes, archiveStatsRes] = await Promise.all([
        axios.get(`${API}/events?include_cancelled=true`, config),  // Admin sees all events including cancelled
        axios.get(`${API}/admin/attendees`, config),
        axios.get(`${API}/admin/participants`, config),
        axios.get(`${API}/admin/scraped-events`, config),
        axios.get(`${API}/admin/attendance-requests`, config),
        axios.get(`${API}/admin/administrators`, config),
        axios.get(`${API}/admin/monday-sync-status`, config),
        axios.get(`${API}/admin/change-alerts?status=all`, config),
        axios.get(`${API}/admin/alert-settings`, config),
        axios.get(`${API}/admin/email-templates`, config),
        axios.get(`${API}/admin/applicants`, config),
        axios.get(`${API}/admin/crew-visibility`, config),
        axios.get(`${API}/admin/applicants/archived`, config),
        axios.get(`${API}/admin/applicants/archive-stats`, config)
      ]);

      setEvents(eventsRes.data);
      setAttendeeData(attendeesRes.data);
      setParticipants(participantsRes.data);
      setScrapedEvents(scrapedRes.data);
      setAttendanceRequests(requestsRes.data);
      setAdministrators(adminsRes.data);
      setMondaySyncStatus(syncRes.data);
      setChangeAlerts(alertsRes.data);
      setAlertRecipients((alertSettingsRes.data && alertSettingsRes.data.emails) || []);
      setEmailTemplates(templatesRes.data || []);
      setApplicants(applicantsRes.data || []);
      setCrewVisibility((crewVisRes.data && crewVisRes.data.value) || 'Forum');
      setArchivedApplicants(archivedRes.data || []);
      setArchiveStats(archiveStatsRes.data || { active: 0, archived: 0, new: 0, welcomed: 0 });
    } catch (error) {
      if (error.response?.status === 401) {
        console.error("401 Unauthorized - Session expired");
        toast.error("Session expired. Please login again.");
        localStorage.removeItem('adminToken');
        setTimeout(() => navigate('/admin/login'), 1000);
      } else {
        console.error("Failed to load data:", error);
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveAttendance = async (attendanceId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/attendance/${attendanceId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Attendance approved! Reminder will be sent day before event.");
      loadData();
    } catch (error) {
      toast.error("Failed to approve attendance");
    }
  };

  const handleRejectAttendance = async (attendanceId) => {
    if (!window.confirm("Are you sure you want to reject this attendance request?")) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/attendance/${attendanceId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Attendance rejected");
      loadData();
    } catch (error) {
      toast.error("Failed to reject attendance");
    }
  };

  const openWithdrawDialog = (attendance) => {
    setWithdrawAttendanceId(attendance.id);
    setWithdrawingAttendance(attendance);
    setWithdrawReason("");
    setWithdrawDialogOpen(true);
  };

  const handleWithdrawApproval = async () => {
    if (!withdrawAttendanceId) return;
    try {
      const token = localStorage.getItem('adminToken');
      
      // Check if this is a pending request (rejection) or approved request (withdrawal)
      const isPending = withdrawingAttendance?.status === 'pending';
      
      if (isPending) {
        // For pending requests, use reject endpoint
        await axios.put(`${API}/admin/attendance/${withdrawAttendanceId}/reject`, 
          { reason: withdrawReason || null }, 
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );
        toast.success("Attendance request withdrawn");
      } else {
        // For approved requests, use withdraw endpoint
        await axios.put(`${API}/admin/attendance/${withdrawAttendanceId}/withdraw`, 
          { reason: withdrawReason || null }, 
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );
        toast.success("Approval withdrawn - crew member notified");
      }
      
      setWithdrawDialogOpen(false);
      setWithdrawAttendanceId(null);
      setWithdrawReason("");
      setWithdrawingAttendance(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to withdraw");
    }
  };

  const handleSendBulkEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.subject || !emailForm.body) {
      toast.error("Subject and body are required");
      return;
    }
    
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('adminToken');
      const additionalRecipients = emailForm.additional_recipients
        .split(',')
        .map(e => e.trim())
        .filter(e => e.includes('@'));
      
      const response = await axios.post(`${API}/admin/send-bulk-email`, {
        recipient_type: emailForm.recipient_type,
        subject: emailForm.subject,
        body: emailForm.body,
        additional_recipients: additionalRecipients,
        event_ids: emailForm.event_ids || null,
        events_summary: emailForm.events_summary || null
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success(`Email sent to ${response.data.sent_count} recipients`);
      if (response.data.failed_count > 0) {
        toast.warning(`Failed to send to ${response.data.failed_count} recipients`);
      }
      setEmailForm({ ...emailForm, subject: '', body: '', additional_recipients: '', event_ids: null, events_summary: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/events`, newEvent, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast.success("Event created successfully!");
      setDialogOpen(false);
      setNewEvent({ name: "", date: "", call_time: "", description: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create event");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast.success("Event deleted successfully!");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete event");
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (error) {
      // Ignore errors, still clear local storage
    }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    localStorage.removeItem('adminEmail');
    toast.success("Logged out successfully");
    navigate('/admin/login');
  };

  const handleSendAlert = async (alertId, edits) => {
    try {
      const token = localStorage.getItem('adminToken');
      const config = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };
      if (edits) {
        await axios.put(`${API}/admin/change-alerts/${alertId}`, edits, config);
      }
      const { data } = await axios.post(`${API}/admin/change-alerts/${alertId}/send`, {}, config);
      toast.success(data.message || "Alert sent");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send alert");
    }
  };

  const handleDismissAlert = async (alertId) => {
    if (!window.confirm("Dismiss this alert without sending?")) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/change-alerts/${alertId}/dismiss`, {}, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success("Alert dismissed");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to dismiss alert");
    }
  };

  const handleSaveAlertRecipients = async (emails) => {
    try {
      const token = localStorage.getItem('adminToken');
      const { data } = await axios.put(`${API}/admin/alert-settings`, { emails }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      setAlertRecipients(data.emails || []);
      toast.success("Default recipients saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save recipients");
    }
  };

  const handleSaveEmailTemplate = async (tpl) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/email-templates`, tpl, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success("Template saved");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
    }
  };

  const _authCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }, withCredentials: true });

  const handleAddApplicant = async (data) => {
    try {
      await axios.post(`${API}/admin/applicants`, data, _authCfg());
      toast.success("Applicant added");
      loadData();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add applicant");
      return false;
    }
  };

  const handleDeleteApplicant = async (id) => {
    if (!window.confirm("Remove this applicant?")) return;
    try {
      await axios.delete(`${API}/admin/applicants/${id}`, _authCfg());
      toast.success("Applicant removed");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove applicant");
    }
  };

  const handleUpdateApplicant = async (id, fields) => {
    try {
      await axios.put(`${API}/admin/applicants/${id}`, fields, _authCfg());
      toast.success("Applicant updated");
      loadData();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update applicant");
      return false;
    }
  };

  const handleSendWelcome = async (id, templateId) => {
    try {
      const { data } = await axios.post(`${API}/admin/applicants/${id}/send-welcome`, { template_id: templateId }, _authCfg());
      toast.success(data.message || "Welcome sent");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send welcome");
    }
  };

  const handleScanApplications = async () => {
    try {
      toast.info("Scanning inbox for applications...");
      const { data } = await axios.post(`${API}/admin/applicants/scan`, {}, _authCfg());
      if (data.success) toast.success(data.message); else toast.error(data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to scan inbox");
    }
  };

  const handleUploadApplicantsCSV = async (file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return false;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      toast.info("Importing applicants from CSV...");
      const { data } = await axios.post(`${API}/admin/applicants/upload-csv`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });
      
      if (data.imported > 0) {
        toast.success(`Successfully imported ${data.imported} applicant(s)`);
      }
      if (data.skipped > 0) {
        toast.info(`Skipped ${data.skipped} duplicate/invalid entries`);
      }
      if (data.errors && data.errors.length > 0) {
        console.warn("CSV Import errors:", data.errors);
      }
      
      loadData();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import CSV");
      return false;
    }
  };

  const handleBulkWelcome = async (applicantIds, templateId) => {
    if (!applicantIds || applicantIds.length === 0) {
      toast.error("No applicants selected");
      return false;
    }
    if (!templateId) {
      toast.error("Please select a template");
      return false;
    }
    
    try {
      toast.info(`Sending welcome emails to ${applicantIds.length} applicant(s)...`);
      const { data } = await axios.post(`${API}/admin/applicants/bulk-welcome`, {
        applicant_ids: applicantIds,
        template_id: templateId
      }, _authCfg());
      
      if (data.sent_count > 0) {
        toast.success(`Sent ${data.sent_count} welcome email(s)`);
      }
      if (data.failed_count > 0) {
        toast.warning(`${data.failed_count} failed to send`);
      }
      
      loadData();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send bulk welcome emails");
      return false;
    }
  };

  const handleExportApplicantsCSV = async () => {
    try {
      toast.info("Preparing CSV export...");
      const response = await axios.get(`${API}/admin/applicants/export-csv`, {
        ...(_authCfg()),
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `applicants_export_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export CSV");
    }
  };

  const handleArchiveApplicant = async (applicantId) => {
    try {
      await axios.post(`${API}/admin/applicants/${applicantId}/archive`, {}, _authCfg());
      toast.success("Applicant archived");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive applicant");
    }
  };

  const handleUnarchiveApplicant = async (applicantId) => {
    try {
      await axios.post(`${API}/admin/applicants/${applicantId}/unarchive`, {}, _authCfg());
      toast.success("Applicant restored");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore applicant");
    }
  };

  const handleAutoArchive = async () => {
    try {
      toast.info("Running auto-archive...");
      const { data } = await axios.post(`${API}/admin/applicants/auto-archive`, {}, _authCfg());
      if (data.archived_count > 0) {
        toast.success(data.message);
      } else {
        toast.info("No applicants to archive");
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to run auto-archive");
    }
  };

  const handleUpdateSignupSettings = async (eventId, settings) => {
    try {
      await axios.put(`${API}/admin/events/${eventId}/signup-settings`, settings, _authCfg());
      toast.success("Sign-up settings updated");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update sign-up settings");
    }
  };

  const handleSetCrewVisibility = async (value) => {
    try {
      const { data } = await axios.put(`${API}/admin/crew-visibility`, { value }, _authCfg());
      setCrewVisibility(data.value || 'Forum');
      toast.success(`Crew can now see: ${data.value}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update crew visibility");
    }
  };

  const handleSwitchToCrew = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/switch-to-crew`, {}, _authCfg());
      localStorage.setItem('crewToken', data.access_token);
      localStorage.setItem('crewData', JSON.stringify(data.user_data));
      navigate('/crew/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to open crew view");
    }
  };


  const handleApproveParticipant = async (participantId, approved) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/participants/${participantId}/approve`,
        { participant_id: participantId, approved: approved },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );

      if (approved) {
        toast.success("Participant approved! They will receive an email with access instructions.");
      } else {
        toast.success("Approval revoked");
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update approval status");
    }
  };

  // Open dialog when revoking approval
  const openRevokeApprovalDialog = (participant) => {
    setRevokeParticipantId(participant.id);
    setRevokeParticipantName(`${participant.first_name} ${participant.last_name}`);
    setRevokeReason("");
    setRevokeApprovalDialogOpen(true);
  };

  // Handle the actual revoke with reason
  const handleRevokeApprovalWithReason = async () => {
    if (!revokeParticipantId) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/participants/${revokeParticipantId}/revoke-approval`,
        { reason: revokeReason || null },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success("Approval revoked - participant notified by email");
      setRevokeApprovalDialogOpen(false);
      setRevokeParticipantId(null);
      setRevokeReason("");
      setRevokeParticipantName("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke approval");
    }
  };

  // Handle checkbox change - show dialog for revoke, direct approve for granting
  const handleApprovalCheckboxChange = (participant, checked) => {
    if (checked) {
      // Granting approval - do it directly
      handleApproveParticipant(participant.id, true);
    } else {
      // Revoking approval - show dialog for reason
      openRevokeApprovalDialog(participant);
    }
  };

  const handleToggleAdminRights = async (participantId, participantEmail, isAdmin) => {
    // Prevent removing own admin rights
    if (!isAdmin && participantEmail.toLowerCase() === currentAdminEmail) {
      toast.error("You cannot remove your own admin rights");
      return;
    }
    
    const action = isAdmin ? "grant admin rights to" : "revoke admin rights from";
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/participants/${participantId}/admin-rights?is_admin=${isAdmin}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      
      toast.success(isAdmin ? "Admin rights granted" : "Admin rights revoked");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update admin rights");
    }
  };

  const handleSuspendParticipant = async (participantId, suspended) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/participants/${participantId}/suspend`,
        { participant_id: participantId, suspended: suspended },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );

      toast.success(suspended ? "Participant suspended" : "Suspension lifted");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update suspension status");
    }
  };

  const handleDeleteParticipant = async (participantId) => {
    if (!window.confirm("Are you sure you want to permanently delete this crew member? This cannot be undone.")) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/participants/${participantId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Participant deleted");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete participant");
    }
  };

  const handleResetPassword = async (participantId, participantName) => {
    const newPassword = window.prompt(`Enter new password for ${participantName} (minimum 6 characters):`);
    if (!newPassword) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/participants/${participantId}/reset-password?new_password=${encodeURIComponent(newPassword)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success(`Password reset for ${participantName}. New password: ${newPassword}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  // Bulk actions for participants
  const handleBulkApproveAll = async () => {
    const unapproved = participants.filter(p => !p.approved);
    if (unapproved.length === 0) {
      toast.info("All participants are already approved");
      return;
    }
    if (!window.confirm(`Approve all ${unapproved.length} pending participants?`)) return;
    
    const token = localStorage.getItem('adminToken');
    let successCount = 0;
    for (const p of unapproved) {
      try {
        await axios.put(
          `${API}/admin/participants/${p.id}/approve`,
          { participant_id: p.id, approved: true },
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );
        successCount++;
      } catch (e) { console.error('Bulk participant action failed for participant', p?.id, e); }
    }
    toast.success(`Approved ${successCount} participants`);
    loadData();
  };

  const handleBulkSuspendAll = async () => {
    const active = participants.filter(p => !p.suspended);
    if (active.length === 0) {
      toast.info("All participants are already suspended");
      return;
    }
    if (!window.confirm(`Suspend all ${active.length} active participants?`)) return;
    
    const token = localStorage.getItem('adminToken');
    let successCount = 0;
    for (const p of active) {
      try {
        await axios.put(
          `${API}/admin/participants/${p.id}/suspend`,
          { participant_id: p.id, suspended: true },
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );
        successCount++;
      } catch (e) { console.error('Bulk participant action failed for participant', p?.id, e); }
    }
    toast.success(`Suspended ${successCount} participants`);
    loadData();
  };

  const handleBulkDeleteAll = async () => {
    if (participants.length === 0) {
      toast.info("No participants to delete");
      return;
    }
    if (!window.confirm(`⚠️ DANGER: Permanently delete ALL ${participants.length} participants? This cannot be undone!`)) return;
    if (!window.confirm(`Are you absolutely sure? Type 'DELETE' in your mind and click OK.`)) return;
    
    const token = localStorage.getItem('adminToken');
    let successCount = 0;
    for (const p of participants) {
      try {
        await axios.delete(`${API}/admin/participants/${p.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        successCount++;
      } catch (e) { console.error('Bulk participant action failed for participant', p?.id, e); }
    }
    toast.success(`Deleted ${successCount} participants`);
    loadData();
  };

  // Scraped Events handlers
  const handleRefreshEvents = async () => {
    setScanning(true);
    try {
      const token = localStorage.getItem('adminToken');
      // Scan past 180 days to catch any updates or new events
      const response = await axios.post(`${API}/admin/scraped-events/scan-historical?days=180`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      if (response.data.imported > 0) {
        toast.success(`Found ${response.data.imported} new events!`);
      } else {
        toast.info("No new events found. All events are up to date.");
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refresh events");
    } finally {
      setScanning(false);
    }
  };

  const handleClearAllScrapedEvents = async () => {
    if (!window.confirm("Delete ALL scraped events? This will allow a fresh import.")) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/scraped-events`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("All scraped events deleted");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear events");
    }
  };

  // Update status for scraped events (Calendar Imports tab)
  const handleUpdateScrapedStatus = async (eventId, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/scraped-events/${eventId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success(`Status updated to ${status}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  // Update status for main events (Event Management tab)
  const handleUpdateEventStatus = async (eventId, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/events/${eventId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success(`Event ${status === 'cancelled' ? 'cancelled' : 'restored'} successfully`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update event status");
    }
  };

  const handleConfirmFromLibrary = async (eventId, eventName) => {
    try {
      const token = localStorage.getItem('adminToken');
      toast.info("Generating search links...");
      const response = await axios.post(`${API}/admin/scraped-events/${eventId}/confirm-from-library`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      // Open the Google search in a new tab
      if (response.data.search_url) {
        window.open(response.data.search_url, '_blank');
        toast.success("Search opened in new tab. Find the event and copy the details.");
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate search links");
    }
  };

  const handleUpdateWebsiteInfo = async (eventId, field, value) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/scraped-events/${eventId}/website-info`, 
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success("Website info updated");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update website info");
    }
  };

  const handleDeleteScrapedEvent = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/scraped-events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Event deleted");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete event");
    }
  };

  const toggleRowExpand = (eventId) => {
    setExpandedRows(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  // Get today's date string in YYYY-MM-DD format
  const getTodayString = () => {
    return new Date().toISOString().split('T')[0];
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Smart sorting: Past events first (scroll up to see), then today (visible), then future
  const getSortedEvents = () => {
    const today = getTodayString();
    
    // If user has selected a specific sort field, use that
    if (sortField) {
      return [...scrapedEvents].sort((a, b) => {
        let aVal = a[sortField] || '';
        let bVal = b[sortField] || '';
        
        if (sortDir === 'asc') {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      });
    }
    
    // Default smart sorting: chronological order (past -> today -> future)
    // Past events at top (scroll up), today visible by default, future below
    return [...scrapedEvents].sort((a, b) => {
      const dateA = a.event_date || '9999-99-99';
      const dateB = b.event_date || '9999-99-99';
      return dateA.localeCompare(dateB);
    });
  };

  // Get index where today's events start for scroll positioning
  const getTodayIndex = () => {
    const today = getTodayString();
    const sorted = getSortedEvents();
    // Find first event that is today or in the future
    const idx = sorted.findIndex(e => e.event_date && e.event_date >= today);
    return idx >= 0 ? idx : 0;
  };

  // Scroll to today's events when Calendar Imports tab is opened
  React.useEffect(() => {
    if (tableRef.current && scrapedEvents.length > 0 && !sortField) {
      const todayIdx = getTodayIndex();
      if (todayIdx > 0) {
        // Calculate approximate row height and scroll position
        const rowHeight = 45; // approximate row height in pixels
        const scrollTop = Math.max(0, (todayIdx - 1) * rowHeight);
        tableRef.current.scrollTop = scrollTop;
      }
    }
  }, [scrapedEvents, sortField]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    navigate,
    events,
    setEvents,
    attendeeData,
    setAttendeeData,
    participants,
    setParticipants,
    administrators,
    setAdministrators,
    mondaySyncStatus,
    changeAlerts,
    setChangeAlerts,
    alertRecipients,
    setAlertRecipients,
    emailTemplates,
    handleSaveEmailTemplate,
    applicants,
    handleAddApplicant,
    handleDeleteApplicant,
    handleUpdateApplicant,
    handleSendWelcome,
    handleScanApplications,
    handleUploadApplicantsCSV,
    handleBulkWelcome,
    handleExportApplicantsCSV,
    handleArchiveApplicant,
    handleUnarchiveApplicant,
    handleAutoArchive,
    archivedApplicants,
    archiveStats,
    handleSwitchToCrew,
    handleUpdateSignupSettings,
    crewVisibility,
    handleSetCrewVisibility,
    handleSendAlert,
    handleDismissAlert,
    handleSaveAlertRecipients,
    setMondaySyncStatus,
    scrapedEvents,
    setScrapedEvents,
    attendanceRequests,
    setAttendanceRequests,
    loading,
    setLoading,
    scanning,
    setScanning,
    dialogOpen,
    setDialogOpen,
    sortField,
    setSortField,
    sortDir,
    setSortDir,
    expandedRows,
    setExpandedRows,
    tableRef,
    attendeeSearch,
    setAttendeeSearch,
    attendeeSortField,
    setAttendeeSortField,
    attendeeSortDir,
    setAttendeeSortDir,
    uploading,
    setUploading,
    syncingMonday,
    setSyncingMonday,
    fileInputRef,
    editingEventId,
    setEditingEventId,
    editEventData,
    setEditEventData,
    withdrawDialogOpen,
    setWithdrawDialogOpen,
    withdrawAttendanceId,
    setWithdrawAttendanceId,
    withdrawReason,
    setWithdrawReason,
    withdrawingAttendance,
    setWithdrawingAttendance,
    currentAdminEmail,
    setCurrentAdminEmail,
    revokeApprovalDialogOpen,
    setRevokeApprovalDialogOpen,
    revokeParticipantId,
    setRevokeParticipantId,
    revokeParticipantName,
    setRevokeParticipantName,
    revokeReason,
    setRevokeReason,
    emailForm,
    setEmailForm,
    sendingEmail,
    setSendingEmail,
    newEvent,
    setNewEvent,
    handleEditEvent,
    handleSaveEvent,
    handleSyncMonday,
    handleCancelEdit,
    handleCSVUpload,
    loadData,
    handleApproveAttendance,
    handleRejectAttendance,
    openWithdrawDialog,
    handleWithdrawApproval,
    handleSendBulkEmail,
    handleCreateEvent,
    handleDeleteEvent,
    handleLogout,
    handleApproveParticipant,
    openRevokeApprovalDialog,
    handleRevokeApprovalWithReason,
    handleApprovalCheckboxChange,
    handleToggleAdminRights,
    handleSuspendParticipant,
    handleDeleteParticipant,
    handleResetPassword,
    handleBulkApproveAll,
    handleBulkSuspendAll,
    handleBulkDeleteAll,
    handleRefreshEvents,
    handleClearAllScrapedEvents,
    handleUpdateScrapedStatus,
    handleUpdateEventStatus,
    handleConfirmFromLibrary,
    handleUpdateWebsiteInfo,
    handleDeleteScrapedEvent,
    toggleRowExpand,
    getTodayString,
    handleSort,
    getSortedEvents,
    getTodayIndex,
  };
};
